/**
 * Revoked Active Channel Targets Policy (TV1-2446 slice 6, design D4 — UI half)
 *
 * Reads the credential state of every channel integration an ACTIVE batch process targets and
 * records the revoked ones in the shared store, so every surface that renders active runs (the
 * batch-processes list's Active section, and the active-run strip above the publications and
 * inventory lists) disables Retry with a Reconnect call to action instead of failing on the
 * server's `CHANNEL_CREDENTIAL_REVOKED` refusal.
 *
 * Re-reads when the set of (process id, status, channel targets) changes — a batch appearing,
 * leaving, or changing status — not on progress ticks. So a batch that errors again after a
 * retry is re-read, and a credential restored while the batch sits errored shows up on the next
 * change or reload (the server refusal stays authoritative meanwhile).
 *
 * FAIL-OPEN like the server (see `readRevokedChannelTargets`). A read that resolves after the
 * active set changed again is dropped: the newer change has started its own read.
 */

import { Policy, PolicyContext } from '@commandDrivenStateEngine/core/policies';
import { BatchProcessesSharedState } from '../state';
import { BatchProcessesSharedCommand } from '../commands';
import { batchChannelTargets, readRevokedChannelTargets, ReadIntegrationCredentialState } from '../channelTargets';

const activeChannelTargetsKey = ( state: BatchProcessesSharedState ): string =>
  state.activeProcesses
    .map(( process ) => ({ process, targets: batchChannelTargets( process ) }))
    .filter(({ targets }) => targets.length > 0 )
    .map(({ process, targets }) => `${ process.id }:${ process.status }:${ targets.join( ',' ) }` )
    .sort()
    .join( '|' );

const activeChannelTargets = ( state: BatchProcessesSharedState ): string[] =>
  [...new Set( state.activeProcesses.flatMap( batchChannelTargets ))];

export const createRevokedActiveChannelTargetsPolicy = (
  readIntegration: ReadIntegrationCredentialState
): Policy<BatchProcessesSharedState, BatchProcessesSharedCommand> => {
  const refresh = async ( ctx: PolicyContext<BatchProcessesSharedState, BatchProcessesSharedCommand> ) => {
    const state = ctx.getState();
    const key = activeChannelTargetsKey( state );
    const targets = activeChannelTargets( state );
    if ( targets.length === 0 && state.revokedChannelTargets.length === 0 ) return;

    const revoked = await readRevokedChannelTargets( targets, readIntegration );
    if ( activeChannelTargetsKey( ctx.getState()) !== key ) return;

    ctx.dispatch(
      { type: 'batchProcesses/shared/credentials/setRevokedChannelTargets', payload: { integrationIds: revoked }},
      { source: 'api' }
    );
  };

  return {
    name: 'revoked-active-channel-targets',

    init: ( ctx ) => {
      void refresh( ctx );
    },

    shouldReact: ( prev, next ) =>
      activeChannelTargetsKey( prev ) !== activeChannelTargetsKey( next ),

    onStateChange: async ({ ctx }) => {
      await refresh( ctx );
    }
  };
};
